import { useMemo, useState, useEffect } from 'react';
import { useDataOptional } from '../../contexts/DataContext';
import { normDueDate, normStartDate, normEndeavorId, normAssignmentId, normEventId } from '../../contexts/dataUtils';

/**
 * Academic Health hook — computes urgency metrics, status, drawer data.
 *
 * Returns: counts, status/pillText, statusReasons[], topPriorities[],
 *          recommendedAction, nextEvent, plus raw items for deep links.
 */
export default function useAcademicHealth(courseFilter = null) {
  const dataContext = useDataOptional() || {};
  const state = dataContext?.state;
  const coursesById = state?.coursesById || {};
  const dataRefreshTrigger = dataContext?.dataRefreshTrigger || 0;

  // ── Global sync: listen for DOM events from any source ──────────────────
  // Many components (timeline, calendar, course view, modals, activities)
  // dispatch these events after mutations.  We bump a local counter so the
  // useMemo recomputes immediately, keeping every tile/drawer in sync.
  const [syncTick, setSyncTick] = useState(0);
  useEffect(() => {
    const bump = () => setSyncTick(t => t + 1);
    const events = [
      'assignments:updated',          // TileDetailModal, Next7DaysModal, UnifiedActivities, UpcomingAssignments
      'scoutiq:assignmentUpdated',    // scoutiqCourseView, Calendar
      'scoutiq:eventUpdated',         // CenterColumn, ScoutIQDashboard
      'scoutiq:forceRefresh',         // scoutiqCourseView (delete / bulk ops)
    ];
    events.forEach(e => window.addEventListener(e, bump));
    return () => events.forEach(e => window.removeEventListener(e, bump));
  }, []);

  return useMemo(() => {
    if (!state?.assignmentsById) {
      return emptyHealth();
    }

    const now = new Date();
    const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    const in7d = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const ago7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // ── Gather & deduplicate assignments ────────────────────────────────────
    // assignmentsById can hold the same logical assignment under different keys
    // (e.g. keyed by `id` from one response and by `assignmentId` from another).
    // Deduplicate by building a canonical-ID map; prefer the richer record.
    const rawAssignments = Object.values(state.assignmentsById).filter(a => {
      if (!a || a.isDeleted) return false;
      if (courseFilter) return normEndeavorId(a) === courseFilter;
      return true;
    });

    const deduped = new Map();
    rawAssignments.forEach(a => {
      // Canonical key: prefer explicit assignmentId fields, fall back to id
      const explicit = a.assignmentId ?? a.assignmentID ?? a.assignment_id;
      const fallback = a.id;
      const canonKey = String(explicit ?? fallback ?? '');
      if (!canonKey) return;

      const existing = deduped.get(canonKey);
      if (!existing) {
        // Also index by `id` so a later richer record can claim this slot
        deduped.set(canonKey, a);
        if (fallback != null && String(fallback) !== canonKey) {
          deduped.set(String(fallback), a);
        }
      } else {
        // Keep whichever record has more fields (richer data)
        if (Object.keys(a).length > Object.keys(existing).length) {
          deduped.set(canonKey, a);
          if (fallback != null) deduped.set(String(fallback), a);
        }
      }
    });

    // Extract unique assignment objects (Map values can repeat, so dedupe refs)
    const seenRefs = new Set();
    const allAssignments = [];
    deduped.forEach(a => {
      if (!seenRefs.has(a)) {
        seenRefs.add(a);
        allAssignments.push(a);
      }
    });

    const overdueItems = [];     // { assignment, dueDate, ageDays }
    const dueNext48hItems = [];
    const dueNext7dItems = [];
    const completedLast7dItems = []; // { assignment, completedDate }
    const next7dCompletedItems = []; // completed assignments whose due date falls in next 7d
    let completedLast7dCount = 0;

    allAssignments.forEach(a => {
      const dueDate = normDueDate(a);
      const completed = isCompleted(a);

      if (completed) {
        // Track completed assignments whose due date falls in next 7 days (for progress tile)
        if (dueDate) {
          const d = new Date(dueDate);
          if (d >= now && d <= in7d) {
            const cAt = a.completedAt || a.completed_at || a.updatedAt || a.updated_at;
            next7dCompletedItems.push({ assignment: a, dueDate: d, completedDate: cAt ? new Date(cAt) : null });
          }
        }

        const completedAt = a.completedAt || a.completed_at || a.updatedAt || a.updated_at;
        if (completedAt) {
          const cDate = new Date(completedAt);
          if (cDate >= ago7d && cDate <= now) {
            completedLast7dCount++;
            completedLast7dItems.push({ assignment: a, completedDate: cDate, dueDate: dueDate ? new Date(dueDate) : null });
          }
        }
        return;
      }

      if (!dueDate) return;
      const d = new Date(dueDate);

      if (d < now) {
        const ageDays = Math.floor((now - d) / (24 * 60 * 60 * 1000));
        overdueItems.push({ assignment: a, dueDate: d, ageDays });
      } else if (d <= in48h) {
        dueNext48hItems.push({ assignment: a, dueDate: d });
        dueNext7dItems.push({ assignment: a, dueDate: d });
      } else if (d <= in7d) {
        dueNext7dItems.push({ assignment: a, dueDate: d });
      }
    });

    // Sort completed by most recent first
    completedLast7dItems.sort((a, b) => b.completedDate - a.completedDate);
    // Sort next7d completed by completion date descending
    next7dCompletedItems.sort((a, b) => (b.completedDate || 0) - (a.completedDate || 0));

    // Sort overdue by oldest first
    overdueItems.sort((a, b) => a.dueDate - b.dueDate);
    // Sort due-soon by soonest first
    dueNext7dItems.sort((a, b) => a.dueDate - b.dueDate);

    // ── Next 7 Days progress (for combined tile) ────────────────────────────
    const next7dTotal = dueNext7dItems.length + next7dCompletedItems.length;
    const next7dCompleted = next7dCompletedItems.length;
    const next7dRemaining = dueNext7dItems.length;

    const overdueCount = overdueItems.length;
    const dueNext48hCount = dueNext48hItems.length;
    const dueSoon7dCount = dueNext7dItems.length;
    const oldestOverdueAgeDays = overdueItems.length > 0 ? overdueItems[0].ageDays : 0;

    // ── Next class event (deduplicated) ────────────────────────────────────
    let nextEventRaw = null;

    if (state.eventsById) {
      const rawEvents = Object.values(state.eventsById).filter(e => {
        if (!e || e.isDeleted) return false;
        if (courseFilter) return normEndeavorId(e) === courseFilter;
        return true;
      });

      // Deduplicate events the same way as assignments
      const eventDedup = new Map();
      rawEvents.forEach(e => {
        const explicit = e.eventId ?? e.eventID ?? e.event_id;
        const fallback = e.id;
        const canonKey = String(explicit ?? fallback ?? '');
        if (!canonKey) return;
        const existing = eventDedup.get(canonKey);
        if (!existing) {
          eventDedup.set(canonKey, e);
          if (fallback != null && String(fallback) !== canonKey) {
            eventDedup.set(String(fallback), e);
          }
        } else if (Object.keys(e).length > Object.keys(existing).length) {
          eventDedup.set(canonKey, e);
          if (fallback != null) eventDedup.set(String(fallback), e);
        }
      });

      const seenEventRefs = new Set();
      eventDedup.forEach(e => {
        if (seenEventRefs.has(e)) return;
        seenEventRefs.add(e);
        const start = normStartDate(e);
        if (!start) return;
        const s = new Date(start);
        if (s > now) {
          if (!nextEventRaw || s < new Date(normStartDate(nextEventRaw))) {
            nextEventRaw = e;
          }
        }
      });
    }

    // ── Health score (0–100) — strict weighted model ────────────────────────
    //  Start at 100, then subtract penalties / add bonuses.
    //  Overdue count is the DOMINANT driver (uncapped: overdueCount * 3).
    let healthScore = 100;

    // 1) Overdue assignments — largest penalty (uncapped)
    healthScore -= overdueCount * 3;

    // 2) Oldest overdue age — stale work is a huge signal
    healthScore -= Math.min(oldestOverdueAgeDays * 1.5, 20);

    // 3) Next 7 days completion ratio — reward staying ahead
    const completionRatio = next7dTotal > 0 ? next7dCompleted / next7dTotal : 1;
    if (completionRatio >= 0.9) {
      healthScore += 10;
    } else if (completionRatio >= 0.6) {
      healthScore += 5;
    } else if (completionRatio < 0.3) {
      healthScore -= 10;
    }

    // 4) Upcoming deadline risk — lots due very soon
    if (dueNext48hCount >= 3) {
      healthScore -= 10;
    }

    // 5) Clamp
    healthScore = Math.max(0, Math.min(100, healthScore));

    // 6) Score → status
    let status, pillText;
    if (healthScore >= 80) {
      status = 'good'; pillText = 'On Track';
    } else if (healthScore >= 55) {
      status = 'warning'; pillText = 'Warning';
    } else {
      status = 'critical'; pillText = 'Critical';
    }

    // ── Snapshot: Problems ──────────────────────────────────────────────────
    const problems = [];
    if (overdueCount > 0) {
      problems.push({
        key: 'overdue',
        text: `${overdueCount} overdue assignment${overdueCount > 1 ? 's' : ''}`,
        value: overdueCount,
      });
    }
    if (next7dRemaining > 0) {
      problems.push({
        key: 'remaining7d',
        text: `${next7dRemaining} remaining due in next 7 days`,
        value: next7dRemaining,
      });
    }
    if (oldestOverdueAgeDays >= 3 && overdueCount > 0) {
      problems.push({
        key: 'oldestAge',
        text: `Oldest overdue: ${oldestOverdueAgeDays} day${oldestOverdueAgeDays > 1 ? 's' : ''} old`,
        value: oldestOverdueAgeDays,
      });
    }
    if (dueNext48hCount >= 3) {
      problems.push({
        key: 'deadlineRisk',
        text: `${dueNext48hCount} due in next 48 hours`,
        value: dueNext48hCount,
      });
    }

    // ── Snapshot: Wins ──────────────────────────────────────────────────────
    const wins = [];
    if (completedLast7dCount > 0) {
      wins.push({
        key: 'completed7d',
        text: `${completedLast7dCount} completed last week`,
      });
    }
    if (next7dCompleted > 0 && next7dTotal > 0) {
      wins.push({
        key: 'next7dProgress',
        text: `${next7dCompleted}/${next7dTotal} next-7-day items done`,
      });
    }
    if (overdueCount === 0) {
      wins.push({
        key: 'noneOverdue',
        text: 'No overdue items — nice!',
      });
    }
    if (wins.length === 0) {
      wins.push({
        key: 'fallback',
        text: 'Keep going, every completion counts',
      });
    }

    // Legacy statusReasons (kept for backward compat)
    const statusReasons = problems.map(p => p.text);
    if (statusReasons.length === 0) statusReasons.push('No urgent items — you\u2019re on track');

    // ── Top priorities (for drawer Section B, max 3) ────────────────────────
    const topPriorities = [];

    // 1) Oldest overdue assignment
    if (overdueItems.length > 0) {
      const item = overdueItems[0];
      const a = item.assignment;
      topPriorities.push({
        type: 'assignment',
        label: `Overdue: ${a.title || a.name || 'Assignment'}`,
        sublabel: `${item.ageDays}d overdue`,
        courseId: normEndeavorId(a),
        assignmentId: normAssignmentId(a),
      });
    }

    // 2) Next due assignment (soonest incomplete, excluding overdue)
    if (dueNext7dItems.length > 0) {
      const item = dueNext7dItems[0];
      const a = item.assignment;
      topPriorities.push({
        type: 'assignment',
        label: `Due soon: ${a.title || a.name || 'Assignment'}`,
        sublabel: formatShortDate(item.dueDate),
        courseId: normEndeavorId(a),
        assignmentId: normAssignmentId(a),
      });
    }

    // 3) Next upcoming class event
    if (nextEventRaw) {
      topPriorities.push({
        type: 'event',
        label: `Next class: ${nextEventRaw.title || nextEventRaw.name || 'Class Meeting'}`,
        sublabel: formatShortDate(new Date(normStartDate(nextEventRaw))),
        courseId: normEndeavorId(nextEventRaw),
        eventId: normEventId(nextEventRaw),
      });
    }

    // ── Recommended action (drawer footer) — references #1 priority ────────
    let recommendedAction = '';
    let recommendedTarget = null; // { type, courseId, assignmentId/eventId }
    if (overdueItems.length > 0) {
      const top = overdueItems[0].assignment;
      const title = top.title || top.name || 'Assignment';
      recommendedAction = `Start here: ${title} (oldest overdue: ${overdueItems[0].ageDays}d)`;
      recommendedTarget = {
        type: 'assignment', courseId: normEndeavorId(top), assignmentId: normAssignmentId(top),
      };
    } else if (dueNext48hCount > 0 && dueNext48hItems.length > 0) {
      const top = dueNext48hItems[0].assignment;
      recommendedAction = `Focus on: ${top.title || top.name || 'Assignment'} — due soon`;
      recommendedTarget = {
        type: 'assignment', courseId: normEndeavorId(top), assignmentId: normAssignmentId(top),
      };
    } else if (dueSoon7dCount > 0 && dueNext7dItems.length > 0) {
      const top = dueNext7dItems[0].assignment;
      recommendedAction = `Stay ahead: ${top.title || top.name || 'Assignment'}`;
      recommendedTarget = {
        type: 'assignment', courseId: normEndeavorId(top), assignmentId: normAssignmentId(top),
      };
    } else {
      recommendedAction = 'All clear this week. Review your plan or add new goals.';
    }

    const hasAssignments = allAssignments.length > 0;

    return {
      overdueCount,
      dueNext48hCount,
      dueSoon7dCount,
      completedLast7dCount,
      oldestOverdueAgeDays,
      healthScore: Math.round(healthScore),
      nextEvent: nextEventRaw
        ? {
            ...nextEventRaw,
            title: nextEventRaw.title || nextEventRaw.name || 'Class Meeting',
            date: normStartDate(nextEventRaw),
            courseId: normEndeavorId(nextEventRaw) || null,
            eventId: normEventId(nextEventRaw) || null,
            courseName: (() => {
              const cid = normEndeavorId(nextEventRaw);
              if (!cid) return null;
              const c = coursesById[cid] || coursesById[String(cid)];
              return c?.name || c?.title || null;
            })(),
          }
        : null,
      status,
      pillText,
      problems,
      wins,
      statusReasons,
      topPriorities,
      recommendedAction,
      recommendedTarget,
      hasAssignments,
      // Raw item arrays for tile detail modals
      overdueItems,
      dueNext7dItems,
      completedLast7dItems,
      // Next 7 Days progress tile
      next7dTotal,
      next7dCompleted,
      next7dRemaining,
      next7dCompletedItems,
    };
  }, [state, courseFilter, dataRefreshTrigger, syncTick]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function isCompleted(a) {
  if (!a) return false;
  if (a.status === 'completed' || a.status === 'Completed') return true;
  if (a.completedAt != null || a.completed_at != null) return true;
  if (a.completed) return true;
  if (a.submissionStatus === 'Completed') return true;
  if (a.completionStatus === 'Completed') return true;
  return false;
}

function emptyHealth() {
  return {
    overdueCount: 0,
    dueNext48hCount: 0,
    dueSoon7dCount: 0,
    completedLast7dCount: 0,
    oldestOverdueAgeDays: 0,
    healthScore: 100,
    nextEvent: null,
    status: 'good',
    pillText: 'No data',
    problems: [],
    wins: [{ key: 'fallback', text: 'No data yet' }],
    statusReasons: ['No data yet'],
    topPriorities: [],
    recommendedAction: 'Add assignments to get started.',
    recommendedTarget: null,
    hasAssignments: false,
    overdueItems: [],
    dueNext7dItems: [],
    completedLast7dItems: [],
    next7dTotal: 0,
    next7dCompleted: 0,
    next7dRemaining: 0,
    next7dCompletedItems: [],
  };
}

function formatShortDate(d) {
  try {
    const wday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${wday} ${time}`;
  } catch {
    return '';
  }
}

